import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import ChatConsole from "@/pages/ChatConsole";
import Documents from "@/pages/Documents";
import ContractReview from "@/pages/ContractReview";
import Matters from "@/pages/Matters";
import MemoBuilder from "@/pages/MemoBuilder";
import AdminConsole from "@/pages/AdminConsole";
import Workflows from "@/pages/Workflows";
import WorkflowExecution from "@/pages/WorkflowExecution";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Configure axios defaults
axios.defaults.withCredentials = true;
// Set base URL for axios - use relative URLs if BACKEND_URL is empty
if (BACKEND_URL) {
  axios.defaults.baseURL = BACKEND_URL;
}

// Auth Context
export const AuthContext = React.createContext(null);

// Auth Callback Component - Processes session_id from Emergent OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      console.log("[AUTH] Processing auth callback, hash:", hash?.substring(0, 50));
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        console.log("[AUTH] Session ID found:", sessionId?.substring(0, 20) + "...");
        
        try {
          console.log("[AUTH] Sending session exchange request to:", `${BACKEND_URL}/api/auth/session`);
          
          const response = await axios.post(`${BACKEND_URL}/api/auth/session`, {
            session_id: sessionId
          });
          
          console.log("[AUTH] Response received:", response.data);
          
          if (response.data.success) {
            toast.success("Welcome to TALC!");
            navigate("/dashboard", { state: { user: response.data.user }, replace: true });
          } else {
            console.error("[AUTH] Auth response not successful:", response.data);
            toast.error("Authentication failed - please try again");
            navigate("/", { replace: true });
          }
        } catch (error) {
          console.error("[AUTH] Auth error details:", {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status
          });
          toast.error(error.response?.data?.detail || "Authentication failed - please try again");
          navigate("/", { replace: true });
        }
      } else {
        console.log("[AUTH] No session ID in hash");
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate, location]);

  return (
    <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#94A3B8] font-manrope">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    // Skip if user passed from AuthCallback
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Router - Handles session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/chat" 
        element={
          <ProtectedRoute>
            <ChatConsole />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/chat/:chatId" 
        element={
          <ProtectedRoute>
            <ChatConsole />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/documents" 
        element={
          <ProtectedRoute>
            <Documents />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contract-review/:documentId" 
        element={
          <ProtectedRoute>
            <ContractReview />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/matters" 
        element={
          <ProtectedRoute>
            <Matters />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/matters/:matterId" 
        element={
          <ProtectedRoute>
            <Matters />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/memo/:chatId" 
        element={
          <ProtectedRoute>
            <MemoBuilder />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminConsole />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workflows" 
        element={
          <ProtectedRoute>
            <Workflows />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/workflows/execution/:executionId" 
        element={
          <ProtectedRoute>
            <WorkflowExecution />
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
